import { useSortableTable } from './hooks/useSortableTable';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigateWithPreference } from './utils/navigationHelper';
import { apiFetch } from './utils/api';
import PageHeader from './components/PageHeader';

function PPMQuotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  
  // Sorting
  const { items: sortedQuotes, requestSort, getSortIndicator } = useSortableTable(quotes, { key: 'quote_ref', direction: 'asc' });
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const columnMenuRef = useRef(null);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    quoteRef: true,
    customer: true,
    site: true,
    totalValue: true,
    contractYears: true,
    status: true,
    date: true,
  });

  // Available columns configuration
  const columns = [
    { key: 'quoteRef', label: 'Quote Ref' },
    { key: 'customer', label: 'Customer' },
    { key: 'site', label: 'Site' },
    { key: 'totalValue', label: 'Total Value' },
    { key: 'contractYears', label: 'Contract Years' },
    { key: 'status', label: 'Status' },
    { key: 'date', label: 'Date' },
  ];

  useEffect(() => {
    fetchQuotes();
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await apiFetch('/api/preferences/default/ppm_quotes');
      const data = await response.json();
      
      if (data.preferences) {
        setVisibleColumns(data.preferences);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const savePreferences = async (newPreferences) => {
    try {
      await apiFetch('/api/preferences/default/ppm_quotes', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: newPreferences })
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  // Save column visibility to API whenever it changes
  useEffect(() => {
    savePreferences(visibleColumns);
  }, [visibleColumns]);

  // Close column menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await apiFetch('/api/ppm/quotes');
      const data = await response.json();
      setQuotes(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setLoading(false);
    }
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const filteredQuotes = sortedQuotes.filter(quote =>
    quote.quote_ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    quote.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    quote.site_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    quote.quote_status?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Count visible columns for colSpan
  const visibleColumnCount = Object.values(visibleColumns).filter(Boolean).length + 1; // +1 for Action column

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader 
        title="PPM Quotes"
        subtitle={`${quotes.length} total`}
        backButton
        onBack={() => navigate('/ppm')}
        backTitle="Back to PPM"
        sticky
        actionButton={
          <button
            onClick={() => navigate('/ppm-quotes/new')}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm"
          >
            ➕ New Quote
          </button>
        }
      />

      <div className="px-4 py-3">
        {/* Search + Column Selector */}
        <div className="mb-3 flex items-center space-x-2">
          <input
            type="text"
            placeholder="Search quotes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 max-w-md px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-violet-500 focus:border-violet-500"
          />
          
          {/* Column Visibility Button */}
          <div className="relative" ref={columnMenuRef}>
            <button
              onClick={() => setShowColumnMenu(!showColumnMenu)}
              className="px-3 py-1.5 bg-gray-300 hover:bg-gray-400 text-black  rounded text-sm font-medium text-black transition flex items-center space-x-1"
            >
              <span>⚙️</span>
              <span>Columns</span>
            </button>

            {/* Dropdown Menu */}
            {showColumnMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 w-48">
                <div className="p-2">
                  <div className="text-xs font-semibold text-gray-600 mb-2 px-2">Show Columns</div>
                  {columns.map(column => (
                    <label
                      key={column.key}
                      className="flex items-center px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns[column.key]}
                        onChange={() => toggleColumn(column.key)}
                        className="mr-2 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                      />
                      <span className="text-sm text-gray-700">{column.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded border border-gray-200">
          <div className="overflow-auto max-h-[calc(100vh-240px)] relative">
            <table className="w-full text-sm table-fixed">
              <thead className="bg-gray-300 sticky top-0 z-20 border-b border-gray-400">
              <tr>
                {visibleColumns.quoteRef && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('quote_ref')}>Quote Ref{getSortIndicator('quote_ref')}</th>}
                {visibleColumns.customer && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('company_name')}>Customer{getSortIndicator('company_name')}</th>}
                {visibleColumns.site && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('site_name')}>Site{getSortIndicator('site_name')}</th>}
                {visibleColumns.totalValue && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('total_payable')}>Total Value{getSortIndicator('total_payable')}</th>}
                {visibleColumns.contractYears && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('contract_years')}>Years{getSortIndicator('contract_years')}</th>}
                {visibleColumns.status && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('quote_status')}>Status{getSortIndicator('quote_status')}</th>}
                {visibleColumns.date && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('quote_date')}>Date{getSortIndicator('quote_date')}</th>}
                <th className="text-right px-3 py-1.5 text-xs font-semibold text-gray-600">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={visibleColumnCount} className="px-3 py-6 text-center text-gray-500 text-sm">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600 mx-auto mb-2"></div>
                    Loading...
                  </td>
                </tr>
              ) : filteredQuotes.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumnCount} className="px-3 py-6 text-center text-gray-500 text-sm">
                    {searchTerm ? 'No matches.' : 'No quotes yet.'}
                  </td>
                </tr>
              ) : (
                filteredQuotes.map((quote, index) => (
                  <tr
                    key={quote.id}
                    onClick={() => navigateWithPreference(navigate, `/ppm-quotes/${quote.id}`, 'ppm-quotes')}
                    className={`hover:bg-blue-200 cursor-pointer transition ${index % 2 === 1 ? 'bg-blue-100' : 'bg-white'}`}
                  >
                    {visibleColumns.quoteRef && (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{quote.quote_ref}</div>
                      </td>
                    )}
                    {visibleColumns.customer && <td className="px-3 py-2 whitespace-nowrap text-gray-700">{quote.company_name || '-'}</td>}
                    {visibleColumns.site && <td className="px-3 py-2 whitespace-nowrap text-gray-700">{quote.site_name || '-'}</td>}
                    {visibleColumns.totalValue && (
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                        £{parseFloat(quote.total_payable || 0).toFixed(2)}
                      </td>
                    )}
                    {visibleColumns.contractYears && (
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                        {quote.contract_years} {quote.contract_years === 1 ? 'year' : 'years'}
                      </td>
                    )}
                    {visibleColumns.status && (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          quote.quote_status === 'accepted' ? 'bg-green-100 text-green-800' :
                          quote.quote_status === 'sent' ? 'bg-blue-100 text-blue-800' :
                          quote.quote_status === 'declined' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {quote.quote_status || 'draft'}
                        </span>
                      </td>
                    )}
                    {visibleColumns.date && (
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                        {new Date(quote.quote_date || quote.created_at).toLocaleDateString('en-GB')}
                      </td>
                    )}
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/ppm-quotes/${quote.id}`);
                        }}
                        className="text-violet-600 hover:text-violet-800 text-xs font-medium mr-3"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>

        <div className="mt-2 text-xs text-gray-600">
          Showing {filteredQuotes.length} of {quotes.length}
        </div>
      </div>
    </div>
  );
}

export default PPMQuotes;
